import { PaymentProviders } from 'payments/constants/providers';
import { usePaymentProvider } from 'payments/hooks/usePayments';
import { useCloverPaymentForm } from 'payments/providers/CloverPaymentFormProvider';
import { useCommerceHubPaymentForm } from 'payments/providers/CommerceHubPaymentFormProvider';
import { useStripePaymentForm } from 'payments/providers/StripePaymentFormProvider';
import type {
  PartialSaveOptions,
  SaveCardOnFileMutation
} from 'payments/types/form';

/**
 * usePaymentForm exposes the appropriate handler for saving the card on file
 */
export const usePaymentForm = () => {
  const paymentProvider = usePaymentProvider();
  const stripePaymentForm = useStripePaymentForm();
  const commerceHubPaymentForm = useCommerceHubPaymentForm();
  const cloverPaymentForm = useCloverPaymentForm();

  const saveCardOnFileError = (paymentForm: string) => {
    const error = `usePaymentForm must be used within ${paymentForm} context`;
    throw new Error(error);
  };

  let paymentForm;
  let paymentFormError: string;

  switch (paymentProvider) {
    case PaymentProviders.CLOVER:
      paymentForm = cloverPaymentForm;
      paymentFormError = 'CloverPaymentForm';
      break;
    case PaymentProviders.COMMERCE_HUB:
      paymentForm = commerceHubPaymentForm;
      paymentFormError = 'CommerceHubPaymentForm';
      break;
    case PaymentProviders.STRIPE:
      paymentForm = stripePaymentForm;
      paymentFormError = 'StripePaymentForm';
      break;
    default:
      return null;
  }

  return {
    retrieveToken: paymentForm
      ? paymentForm.retrieveToken
      : () => saveCardOnFileError(paymentFormError),
    saveCardOnFile: paymentForm
      ? paymentForm.saveCardOnFile
      : () => saveCardOnFileError(paymentFormError),
    applySavedPaymentMethod: applySavedPaymentMethod()
  };
};

// TODO: This is redundant with handleSaveCardOnFileMutation and should be
// removed in the future
export const applySavedPaymentMethod =
  () =>
  async (saveOptions: PartialSaveOptions, mutate: SaveCardOnFileMutation) => {
    const { firstName, lastName, phoneCode, phone, ...otherOptions } =
      saveOptions;

    const options = {
      ...otherOptions,
      name: `${firstName} ${lastName}`,
      phone: `+${phoneCode}${phone}`
    };

    // NOTE: This callback will call the mutation to save the card on file
    mutate(options);
  };
